import { forwardRef } from 'react';
import { cn } from '../utils/cn';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** `sunken` suppresses the shadow for cards nested inside another card. */
  elevation?: 'flat' | 'card' | 'raised';
  padded?: boolean;
  as?: 'div' | 'section' | 'article' | 'li';
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { elevation = 'card', padded = false, as = 'div', className, children, ...rest },
  ref,
) {
  // `as` produces a union of element types, and TypeScript resolves the props
  // and ref of a union to their intersection — which no single element
  // satisfies. Widening to ElementType keeps the prop surface honest at the
  // component's own boundary (CardProps) while letting the union render.
  const Component = as as React.ElementType;

  return (
    <Component
      ref={ref}
      className={cn(
        'flex min-w-0 flex-col rounded-xl border border-line bg-surface',
        elevation === 'card' && 'shadow-card',
        elevation === 'raised' && 'shadow-raised',
        padded && 'p-4',
        className,
      )}
      {...rest}
    >
      {children}
    </Component>
  );
});

/**
 * `title` is omitted from the inherited attributes because the DOM's `title`
 * is a tooltip string, and a card header's title is renderable content. Keeping
 * the DOM meaning would silently accept a node where a string was expected.
 */
export interface CardHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  /** Small uppercase eyebrow used by the reference dashboards. */
  eyebrow?: boolean;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  headingLevel?: 2 | 3 | 4;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(function CardHeader(
  { title, eyebrow = true, description, actions, headingLevel = 3, className, ...rest },
  ref,
) {
  const Heading = `h${headingLevel}` as const;
  return (
    <div
      ref={ref}
      className={cn(
        'flex items-start justify-between gap-3 border-b border-line-subtle px-4 py-3',
        className,
      )}
      {...rest}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <Heading
          className={cn(
            'truncate',
            eyebrow
              ? 'text-dense font-semibold uppercase tracking-eyebrow text-content-secondary'
              : 'text-section font-semibold text-content',
          )}
        >
          {title}
        </Heading>
        {description ? <p className="text-dense-lg text-content-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
});

export const CardBody = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardBody({ className, ...rest }, ref) {
    return (
      <div ref={ref} className={cn('flex min-w-0 flex-1 flex-col p-4', className)} {...rest} />
    );
  },
);

export const CardFooter = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-between gap-3 border-t border-line-subtle px-4 py-2.5',
          className,
        )}
        {...rest}
      />
    );
  },
);
